import EventEmitter from 'events';
import fscreen from 'fscreen';
import { wrap } from 'lodash';
import SyosEvents from './SyosEvents';
import type SyosController from '../SyosController';
import { Screen } from '../api/types';
import SyosMediaQuery from './SyosMediaQuery';

const maxWidth = 767;

function appendDialogAttributes(el: Element) {
  el.setAttribute('aria-modal', 'true');
  el.setAttribute('role', 'dialog');

  const ariaLabelledByElement = document.querySelector('.tn-section-syos-heading');
  if (ariaLabelledByElement && ariaLabelledByElement.id) {
    const existingAttribute = el.attributes.getNamedItem('aria-labelledby');

    const value = existingAttribute
      ? `${existingAttribute.value} ${ariaLabelledByElement.id}`
      : ariaLabelledByElement.id;

    el.setAttribute('aria-labelledby', value);
  }
}

function getFullScreenRootElement() {
  const className = 'tn-syos-fullscreen-root';
  let el = document.querySelector(`.${className}`);

  if (!el) {
    el = document.createElement('div');
    el.classList.add(className);
    document.body.insertAdjacentElement('beforeend', el);
  }

  appendDialogAttributes(el);
  return el;
}

class SyosFullscreenManager extends EventEmitter {
  fullScreenRootElement: Element;

  fullscreenEnabled: boolean;

  isCurrentlyFullscreen = false;

  syosMediaQuery?: SyosMediaQuery;

  constructor(syosController: SyosController, syosMediaQuery: SyosMediaQuery) {
    super();

    this.syosMediaQuery = syosMediaQuery;

    this.fullScreenRootElement = getFullScreenRootElement();

    const screenWidth = window.screen.width;

    this.fullscreenEnabled = screenWidth <= maxWidth && fscreen.fullscreenEnabled;

    this.wrapMethods();

    if (this.fullscreenEnabled) {
      fscreen.addEventListener('fullscreenchange', this.onFullscreenChange);
      syosController.on(SyosEvents.SCREEN_UPDATED, this.onScreenUpdate);
      syosController.on(SyosEvents.SEAT_LIST_DATA_LOADED, this.onSeatListDataLoaded);
      syosMediaQuery.on(SyosEvents.MOBILE_LEAVE, this.exitFullscreen);
      syosController.setFullScreenIsEnabled();
    }
  }

  wrapMethods() {
    const testFullscreenEnabled = (fn: () => void) => {
      if (this.fullscreenEnabled) {
        return fn();
      }
      return false;
    };

    this.enterFullscreen = wrap(this.enterFullscreen, testFullscreenEnabled);
    this.exitFullscreen = wrap(this.exitFullscreen, testFullscreenEnabled);
    this.toggleFullscreen = wrap(this.toggleFullscreen, testFullscreenEnabled);
  }

  onFullscreenChange = () => {
    this.isCurrentlyFullscreen = fscreen.fullscreenElement !== null;
  };

  onScreenUpdate = ({ screen }: { screen: Screen }) => {
    if (!screen) {
      this.exitFullscreen();
    }
  };

  onSeatListDataLoaded = () => {
    if (this.syosMediaQuery?.testIsMobile()) {
      this.enterFullscreen();
    }
  };

  getFullscreenEnabled() {
    return this.fullscreenEnabled;
  }

  toggleFullscreen = () => {
    if (this.isCurrentlyFullscreen) {
      return this.exitFullscreen();
    }
    return this.enterFullscreen();
  };

  enterFullscreen = () => {
    if (!this.isCurrentlyFullscreen) {
      return fscreen.requestFullscreen(this.fullScreenRootElement);
    }

    return false;
  };

  exitFullscreen = () => {
    if (this.isCurrentlyFullscreen) {
      return fscreen.exitFullscreen();
    }

    return false;
  };

  destroy() {
    this.exitFullscreen();
    this.syosMediaQuery?.removeListener(SyosEvents.MOBILE_LEAVE, this.exitFullscreen);
    this.syosMediaQuery = undefined;
  }
}

export default SyosFullscreenManager;
